import { FC } from 'react';
import { type Quiz } from '@teachur/http-client';
import { formatShortDate } from '@new/common/lib/dateTime';
import { useExamContext } from '../../../../contexts/ExamProvider';

const PERCENTAGE_MULTIPLIER = 100;

interface AttemptItemProps {
  attempt: Quiz;
  previousCorrectCount: number;
  currentCorrectCount: number;
  totalQuestions: number;
}

export const AttemptItem: FC<AttemptItemProps> = ({
  attempt,
  previousCorrectCount,
  currentCorrectCount,
  totalQuestions,
}) => {
  const { setExamMode } = useExamContext();

  const increment = currentCorrectCount - previousCorrectCount;
  const startProgress =
    totalQuestions === 0 ? 0 : (previousCorrectCount / totalQuestions) * PERCENTAGE_MULTIPLIER;
  const endProgress =
    totalQuestions === 0 ? 0 : (currentCorrectCount / totalQuestions) * PERCENTAGE_MULTIPLIER;
  const segmentWidth = endProgress - startProgress;

  const onAttemptClick = (attempt: Quiz) => setExamMode({ type: 'result', attempt });

  return (
    <div
      key={attempt.id}
      onClick={() => onAttemptClick(attempt)}
      className="flex flex-col lg:flex-row items-center gap-4 cursor-pointer! hover:bg-(--primary-O200) transition-colors! duration-300"
    >
      <div className="flex items-center justify-between w-full lg:w-fit">
        <span className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB500)! lg:min-w-27.5">
          {formatShortDate(attempt.createdAt)}
        </span>

        <span className="[font-size:var(--fs-p4)]! lg:[font-size:var(--fs-p3)]! text-(--neutral-black-NB1000)! font-bold lg:hidden">
          {`${currentCorrectCount}/${totalQuestions}`}
        </span>
      </div>

      <div className="relative w-full h-3 lg:h-4.75 bg-(--neutral-black-NB100) rounded-full overflow-hidden">
        <div
          className="absolute h-full bg-(--secondary-B800) rounded-full flex items-center justify-center"
          style={{
            left: `${startProgress}%`,
            width: `${segmentWidth}%`,
          }}
        >
          {increment > 0 && (
            <span className="[font-size:var(--fs-l5)]! text-(--neutral-white-NW1000) font-semibold z-10 pointer-events-none text-center">
              +{increment}
            </span>
          )}
        </div>
      </div>

      <span className="hidden lg:block [font-size:var(--fs-p3)]! text-(--neutral-black-NB800)! font-semibold min-w-12.5 text-right">
        {`${currentCorrectCount}/${totalQuestions}`}
      </span>
    </div>
  );
};
